/**
 * User Input Validation Utility
 * Validates and sanitizes user message input for security
 * Issue #6: Missing Input Validation for User Messages
 */

// Configuration constants
export const INPUT_VALIDATION_CONFIG = {
    MAX_MESSAGE_LENGTH: 4000,
    MIN_MESSAGE_LENGTH: 1,
    MAX_CONSECUTIVE_CHARS: 50, // Prevent spam like "aaaaaaa..."
    RATE_LIMIT_WINDOW_MS: 60000, // 1 minute
    MAX_REQUESTS_PER_WINDOW: 20,
};

// Rate limiting storage
const requestTimestamps = [];

/**
 * Validates and sanitizes user message input
 * @param {string} message - Raw user message
 * @returns {Object} - { valid: boolean, sanitized?: string, error?: string, originalLength?: number, sanitizedLength?: number }
 */
export const validateUserMessage = (message) => {
    // Step 1: Type validation
    if (message === null || message === undefined) {
        return { valid: false, error: "Message is required" };
    }

    if (typeof message !== "string") {
        return { valid: false, error: "Message must be text" };
    }

    // Step 2: Trim and check minimum length
    const trimmed = message.trim();

    if (trimmed.length < INPUT_VALIDATION_CONFIG.MIN_MESSAGE_LENGTH) {
        return { valid: false, error: "Please enter a message" };
    }

    // Step 3: Check maximum length
    if (trimmed.length > INPUT_VALIDATION_CONFIG.MAX_MESSAGE_LENGTH) {
        return {
            valid: false,
            error: `Message is too long (${trimmed.length}/${INPUT_VALIDATION_CONFIG.MAX_MESSAGE_LENGTH} characters)`,
        };
    }

    // Step 4: Remove control characters (security)
    let sanitized = trimmed
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "") // ASCII control chars
        .replace(/[\u200B-\u200D\uFEFF]/g, ""); // Zero-width chars

    // Step 5: Check for excessive repeated characters (spam prevention)
    const repeatedCharRegex = new RegExp(`(.)\\1{${INPUT_VALIDATION_CONFIG.MAX_CONSECUTIVE_CHARS},}`, "g");
    if (repeatedCharRegex.test(sanitized)) {
        sanitized = sanitized.replace(repeatedCharRegex, (match) => match.charAt(0).repeat(INPUT_VALIDATION_CONFIG.MAX_CONSECUTIVE_CHARS));
    }

    // Step 6: Normalize whitespace
    sanitized = sanitized
        .replace(/\t/g, " ") // Replace tabs with spaces
        .replace(/ {3,}/g, "  ") // Max 2 consecutive spaces
        .replace(/\n{4,}/g, "\n\n\n"); // Max 3 consecutive newlines

    return {
        valid: true,
        sanitized,
        originalLength: message.length,
        sanitizedLength: sanitized.length,
    };
};

/**
 * Check if user is rate limited
 * @returns {Object} - { allowed: boolean, retryAfterMs?: number, error?: string }
 */
export const checkRateLimit = () => {
    const now = Date.now();
    const windowStart = now - INPUT_VALIDATION_CONFIG.RATE_LIMIT_WINDOW_MS;

    // Remove old timestamps outside the window
    while (requestTimestamps.length > 0 && requestTimestamps[0] < windowStart) {
        requestTimestamps.shift();
    }

    if (requestTimestamps.length >= INPUT_VALIDATION_CONFIG.MAX_REQUESTS_PER_WINDOW) {
        const oldestInWindow = requestTimestamps[0];
        const retryAfterMs = oldestInWindow + INPUT_VALIDATION_CONFIG.RATE_LIMIT_WINDOW_MS - now;
        return {
            allowed: false,
            retryAfterMs,
            error: `Too many requests. Please wait ${Math.ceil(retryAfterMs / 1000)} seconds.`,
        };
    }

    // Record this request
    requestTimestamps.push(now);
    return { allowed: true };
};

/**
 * Combined validation: message content + rate limiting
 * @param {string} message - User message
 * @returns {Object} - Validation result
 */
export const validateAndSanitizeInput = (message) => {
    // Check rate limit first
    const rateCheck = checkRateLimit();
    if (!rateCheck.allowed) {
        return { valid: false, error: rateCheck.error, rateLimited: true };
    }

    // Validate message content
    return validateUserMessage(message);
};

/**
 * Reset rate limit (useful for testing or clearing state)
 */
export const resetRateLimit = () => {
    requestTimestamps.length = 0;
};

export default {
    validateUserMessage,
    checkRateLimit,
    validateAndSanitizeInput,
    resetRateLimit,
    INPUT_VALIDATION_CONFIG,
};
